import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  SettlementListQuery,
  SettlementRepository,
} from '@app/domain/ports/tpv/settlement.repository';
import { TpvSettlement, TpvSettlementCreated } from '@app/domain/models/tpv/settlement.model';
import { TpvPage } from '@app/domain/models/tpv/tpv-page.model';
import {
  CreateSettlementRequest,
  CreateSettlementResponseDto,
  SettlementDto,
} from '@app/data/dtos/tpv/settlement.dto';
import { mapSettlement } from '@app/data/mappers/tpv/settlement.mapper';
import { toTpvPage } from '@app/data/mappers/tpv/tpv-page.mapper';
import { tpvUrl } from '@app/data/tpv/tpv-api.config';
import { TpvEnvelope, unwrapTpv } from '@app/data/tpv/tpv-envelope';
import { toHttpParams } from './tpv-http.util';

/** `GET /api/v1/tpv/settlements/`. Auth vía interceptores estándar. */
@Injectable()
export class SettlementRemoteDataSource extends SettlementRepository {
  private readonly http = inject(HttpClient);

  override getSettlements(
    externalIdMerchant: string,
    query: SettlementListQuery = {},
  ): Observable<TpvPage<TpvSettlement>> {
    const page = query.page ?? 1;
    const limit = query.limit ?? 10;
    const params = toHttpParams({
      id_merchant: externalIdMerchant,
      page,
      limit,
      start_date: query.startDate,
      end_date: query.endDate,
      merchant_name: query.merchantName,
    });

    return this.http
      .get<TpvEnvelope<unknown>>(tpvUrl('settlements/'), { params })
      .pipe(
        map((raw) =>
          toTpvPage<SettlementDto, TpvSettlement>(raw, mapSettlement, { page, limit }),
        ),
      );
  }

  override getSettlementById(
    idSettlement: string | number,
  ): Observable<TpvSettlement | null> {
    return this.http
      .get<TpvEnvelope<SettlementDto | SettlementDto[]>>(tpvUrl(`settlements/${idSettlement}/`))
      .pipe(
        map((raw) => {
          const data = unwrapTpv<SettlementDto | SettlementDto[]>(raw);
          const dto = Array.isArray(data) ? data[0] : data;
          return dto ? mapSettlement(dto) : null;
        }),
      );
  }

  override createSettlement(
    request: CreateSettlementRequest,
    idempotencyKey?: string,
  ): Observable<TpvSettlementCreated> {
    return this.http
      .post<TpvEnvelope<CreateSettlementResponseDto> | CreateSettlementResponseDto>(
        tpvUrl('settlements/'),
        request,
        { headers: this.buildHeaders(idempotencyKey) },
      )
      .pipe(
        map((raw) => {
          const dto = unwrapTpv<CreateSettlementResponseDto>(raw);
          return { idSettlement: dto?.id_settlement ?? '' };
        }),
      );
  }

  private buildHeaders(idempotencyKey?: string): HttpHeaders {
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    if (idempotencyKey) {
      headers = headers.set('Idempotency-Key', idempotencyKey);
    }
    return headers;
  }
}
